import styled from '@emotion/styled';

import { Text } from '@/components/ui/Text';
import { formatMoney } from '@/lib/currency';
import type { IStay } from '@/lib/stay';
import { colors, font, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';

const Rows = styled.dl`
	display: flex;
	flex-direction: column;
	gap: ${spacing.space20};
	margin: 0;
`;

const Row = styled.div<{ total?: boolean }>`
	display: flex;
	justify-content: space-between;
	gap: ${spacing.space40};
	${({ total }) => (total ? font('IB100') : font('IR100'))}
	color: ${({ total }) => (total ? colors.typography.heading : colors.typography.body)};

	dt,
	dd {
		margin: 0;
	}
	dd {
		white-space: nowrap;
	}
`;

/* Set apart by weight and a little space; the section above already has a rule. */
const TotalRow = styled(Row)`
	padding-top: ${spacing.space20};
`;

const WasPrice = styled.s`
	color: ${colors.LM_NU60};
	font-weight: 400;
	margin-right: ${spacing.space20};
`;

interface PriceBreakdownProps {
	stay: IStay;
	/** A fixed discount that is worth more than the stay cannot be applied. */
	discountInvalid?: boolean;
}

/**
 * The line items that make up the price of a stay, and the total. Shows the
 * pre-discount total struck through when a discount applies.
 */
export const PriceBreakdown = ({
	stay,
	discountInvalid = false,
}: PriceBreakdownProps) => {
	const t = useI18n();
	const currency = stay.currency ?? 'GBP';
	const lineItems = stay.lineItems ?? [];
	const totalBefore = stay.totalBeforeDiscount ?? 0;
	const totalToPay = stay.totalToPayToday ?? 0;
	const hasDiscount = !discountInvalid && totalToPay !== totalBefore;

	return (
		<Rows aria-label={t('booking.price.title')}>
			{lineItems.map((item) => {
				const isDiscount = item.amount < 0;
				return (
					<Row key={item.description}>
						<dt>{item.description}</dt>
						<dd>
							{isDiscount && discountInvalid
								? t('booking.price.notApplicable')
								: `${isDiscount ? '-' : ''}${formatMoney(item.amount, currency)}`}
						</dd>
					</Row>
				);
			})}
			<TotalRow total>
				<dt>{t('booking.price.total', { currency })}</dt>
				<dd>
					{hasDiscount && (
						<WasPrice>
							{formatMoney(totalBefore, currency)}
						</WasPrice>
					)}
					{formatMoney(
						discountInvalid ? totalBefore : totalToPay,
						currency,
					)}
				</dd>
			</TotalRow>
		</Rows>
	);
};

/** Shown in place of the breakdown before dates are chosen or when they can't be booked. */
export const PriceBreakdownPlaceholder = ({ stay }: { stay: IStay }) => {
	const t = useI18n();
	if (stay.isDateSelected && !stay.hasAvailability) {
		return (
			<Text variant="bodySmall" color="error">
				{stay.error ?? t('booking.noAvailability')}
			</Text>
		);
	}
	return (
		<Text variant="bodySmall" color="muted">
			{t('booking.addDatesForPrice')}
		</Text>
	);
};
